import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { useLocation } from 'wouter';
import CONFIG from '@/lib/config';

const AdminLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [, setLocation] = useLocation();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      console.log('🚀 Admin login attempt...');
      console.log('📧 Email:', email);
      console.log('🌐 API URL:', `${CONFIG.API_BASE_URL}/api/admin/login`);
      console.log('🌍 Current origin:', window.location.origin);
      
      const response = await fetch(`${CONFIG.API_BASE_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
        credentials: 'include',
      });

      console.log('📡 Response status:', response.status);
      console.log('📡 Response headers:', Object.fromEntries(response.headers.entries()));

      const data = await response.json();
      console.log('📊 Response data:', data);

      if (response.ok && data.success) {
        // Store admin token (JWT) as fallback for cross-origin requests
        // Primary authentication is via session cookies, but JWT token provides fallback
        if (data.token) {
          const storeCredentials = (token: string, user: any) => {
            try {
              localStorage.setItem('adminToken', token);
              localStorage.setItem('adminUser', JSON.stringify(user));
              console.log('✅ Stored credentials in localStorage');
              return true;
            } catch (error) {
              console.warn('localStorage blocked, using sessionStorage fallback');
              try {
                sessionStorage.setItem('adminToken_session', token);
                sessionStorage.setItem('adminUser_session', JSON.stringify(user));
                console.log('✅ Stored credentials in sessionStorage');
                return true;
              } catch (sessionError) {
                console.error('All storage methods blocked:', sessionError);
                return false;
              }
            }
          };
          storeCredentials(data.token, data.user);
        }

        // Session-based authentication (primary method)
        console.log('✅ Admin login successful, session created');
        console.log('🍪 Session cookie should be set automatically by browser');
        
        // Redirect to admin dashboard (session is automatically handled by cookies)
        console.log('🔄 Redirecting to /admin...');
        setLocation('/admin');
      } else {
        // Provide more helpful error messages
        if (data.error === 'Invalid credentials') {
          setError('❌ Invalid email or password. Please check your credentials below.');
        } else {
          setError(data.error || 'Login failed. Please try again.');
        }
      }
    } catch (error) {
      console.error('❌ Admin login error:', error);
      console.error('❌ Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      
      // 检查是否是JSON解析错误（通常是服务器返回HTML错误页面）
      if (error.message.includes('Unexpected token') && error.message.includes('<!DOCTYPE')) {
        setError('❌ 服务器连接错误：后端服务器可能未正常运行。请检查后端服务器状态。');
      } else if (error.message.includes('Failed to fetch')) {
        setError('❌ 网络连接错误：无法连接到后端服务器。请检查服务器是否运行在 http://localhost:5001');
      } else {
        setError('❌ 登录失败：' + error.message);
      }
      
      // 提供更精确的错误提示
      let errorMessage = '';
      if (error.message.includes('Failed to fetch') || error.message.includes('ERR_CONNECTION_REFUSED')) {
        errorMessage = '🔌 后端服务未启动：请确保后端服务正在运行 (localhost:5001)';
      } else if (error.message.includes('NetworkError')) {
        errorMessage = '🌐 网络连接错误：请检查网络连接';
      } else if (error.message.includes('timeout')) {
        errorMessage = '⏱️ 请求超时：请稍后重试';
      } else {
        errorMessage = `❌ 登录失败：${error.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-900 via-red-800 to-red-700 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-2xl">
          <CardHeader className="text-center pb-6">
            <div className="mx-auto w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">
              Admin Console
            </CardTitle>
            <p className="text-gray-600 mt-2">
              Sign in to access the administration panel
            </p>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="admin@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 border-gray-300 focus:border-red-500 focus:ring-red-500"
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 border-gray-300 focus:border-red-500 focus:ring-red-500"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Signing in...
                  </div>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default AdminLogin;
